/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { Button, VerticalDivider, Inline, toTitleCase } from 'sarsaparilla';
import get from 'lodash/get';
import SearchFilterPill from './SearchFilterPill';
import * as searchActions from '../actions/search';
import { FLAT_CAMPING_FILTERS } from '../constants/filters/camping';

export class SearchFilterPills extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func,
        search: PropTypes.object,
    };

    static displayName = 'SearchFilterPills';

    constructor(props) {
        super(props);
        this.state = {
            viewAll: false,
        };
        this.renderMainSearchFilters = this.renderMainSearchFilters.bind(this);
        this.clearAll = this.clearAll.bind(this);
        this.viewAllFilters = this.viewAllFilters.bind(this);
        this.onFilterRemove = this.onFilterRemove.bind(this);
    }

    clearAll() {
        this.props.dispatch(searchActions.resetSearchFilters());
        this.props.dispatch(
            searchActions.updateSearchCriterias({
                checkin_time: '',
                checkout_time: '',
                avail_nights: '',
                avail_months: [],
                flex_start_range: '',
                flex_end_range: '',
                tripPreferences: false,
                fg: [],
                ai_query: '',
                what: '',
            })
        );
        setTimeout(() => {
            this.props.dispatch(searchActions.navigateToSearchResults());
        }, 100);
    }

    viewAllFilters() {
        this.setState({ viewAll: true });
    }

    onFilterRemove(uiFilterName, filter) {
        if (
            uiFilterName === 'include_unavailable' ||
            uiFilterName === 'include_notreservable' ||
            uiFilterName === 'include_partially_available'
        ) {
            this.props.dispatch(searchActions.updateSearchCriteria(uiFilterName, true));
        } else if (
            uiFilterName === 'occupants' ||
            uiFilterName === 'campsiteVehicleLength' ||
            uiFilterName === 'tourDuration'
        ) {
            this.props.dispatch(searchActions.removeSearchFilter(uiFilterName, filter));
        } else if (uiFilterName === 'tourTimes') {
            this.props.dispatch(searchActions.removeTourTimesFilter(filter));
        } else {
            this.props.dispatch(searchActions.toggleSearchFilter(uiFilterName, filter));
        }
        this.props.dispatch(searchActions.navigateToSearchResults());
    }

    renderMainSearchFilters() {
        const pills = [];

        if (this.props.search?.uiFilters) {
            const uiFilters = this.props.search.uiFilters;

            if (Object.keys(uiFilters.tourTypes).length) {
                Object.keys(uiFilters.tourTypes).forEach((k) => {
                    const flt = uiFilters.tourTypes[k];
                    pills.push(
                        <SearchFilterPill
                            key={flt.value}
                            title={flt.value}
                            uiFilterName={'tourTypes'}
                            filter={flt}
                            onRemove={this.onFilterRemove}
                        />
                    );
                });
            }

            if (Object.keys(uiFilters.tourDifficulty).length) {
                Object.keys(uiFilters.tourDifficulty).forEach((k) => {
                    const flt = uiFilters.tourDifficulty[k];
                    pills.push(
                        <SearchFilterPill
                            key={flt.value}
                            title={flt.value}
                            uiFilterName={'tourDifficulty'}
                            filter={flt}
                            onRemove={this.onFilterRemove}
                        />
                    );
                });
            }

            if (Object.keys(uiFilters.tourDuration).length) {
                pills.push(
                    <SearchFilterPill
                        key={'Tour Duration'}
                        title={'Tour Duration'}
                        uiFilterName={'tourDuration'}
                        filter={uiFilters.tourDuration['tour-duration']}
                        onRemove={this.onFilterRemove}
                    />
                );
            }

            if (Object.keys(uiFilters.tourPrice).length) {
                pills.push(<SearchFilterPill key={'Tour Price'} title={'Tour Price'} />);
            }

            if (Object.keys(uiFilters.tourTime).length) {
                pills.push(<SearchFilterPill key={'Tour Time'} title={'Tour Time'} />);
            }

            if (uiFilters?.tourTimes && Object.keys(uiFilters.tourTimes).length) {
                pills.push(
                    <SearchFilterPill
                        key={'Tour Times'}
                        title={'Tour Times'}
                        uiFilterName={'tourTimes'}
                        filter={uiFilters.tourTimes}
                        onRemove={this.onFilterRemove}
                    />
                );
            }

            if (Object.keys(uiFilters.permitTypes).length) {
                Object.keys(uiFilters.permitTypes).forEach((k) => {
                    const flt = uiFilters.permitTypes[k];
                    pills.push(
                        <SearchFilterPill
                            key={flt.value}
                            title={flt.value}
                            uiFilterName={'permitTypes'}
                            filter={flt}
                            onRemove={this.onFilterRemove}
                        />
                    );
                });
            }

            if (Object.keys(uiFilters.activities).length) {
                Object.keys(uiFilters.activities).forEach((k) => {
                    const flt = uiFilters.activities[k];
                    pills.push(
                        <SearchFilterPill
                            key={flt.value}
                            title={flt.value}
                            uiFilterName={'activities'}
                            filter={flt}
                            onRemove={this.onFilterRemove}
                        />
                    );
                });
            }

            if (uiFilters.activityTags && Object.keys(uiFilters.activityTags).length) {
                Object.keys(uiFilters.activityTags).forEach((k) => {
                    const flt = uiFilters.activityTags[k];
                    pills.push(
                        <SearchFilterPill
                            key={`activity-tag-${flt.key}`}
                            title={flt.value}
                            uiFilterName={'activityTags'}
                            filter={flt}
                            onRemove={this.onFilterRemove}
                        />
                    );
                });
            }

            if (uiFilters?.agencies && Object.keys(uiFilters.agencies).length) {
                Object.keys(uiFilters.agencies).forEach((k) => {
                    const flt = uiFilters.agencies[k];
                    pills.push(
                        <SearchFilterPill
                            key={flt.value}
                            title={`${flt.displayName} ${
                                flt.hasCode ? ` (${flt.key})` : ''
                            }`}
                            uiFilterName={'agencies'}
                            filter={flt}
                            onRemove={this.onFilterRemove}
                        />
                    );
                });
            }
        }

        const filterGroups = get(this.props.search, 'fg', []);

        if (filterGroups.length > 0) {
            FLAT_CAMPING_FILTERS.forEach((item) => {
                if (filterGroups.indexOf(item.key) !== -1) {
                    pills.push(
                        <SearchFilterPill
                            key={item.key}
                            filter={item.key}
                            title={item.value}
                            isFgParam
                        />
                    );
                }
            });
            const occupants = filterGroups.find((item) => item.includes('group-size'));
            if (occupants) {
                const value = occupants.split(':');
                pills.push(
                    <SearchFilterPill
                        key={`occupants`}
                        filter="group-size"
                        title={`${value[1]} Occupants`}
                        isFgParam
                    />
                );
            }
            const vehicleLength = filterGroups.find((item) =>
                item.includes('vehicle-length')
            );
            if (vehicleLength) {
                const value = vehicleLength.split(':');
                pills.push(
                    <SearchFilterPill
                        key={'campsiteVehicleLength'}
                        filter={'vehicle-length'}
                        title={`Vehicle Length: ${value[1]} ft`}
                        isFgParam
                    />
                );
            }
        }

        const hasActivityTagsInUiFilters =
            this.props.search?.uiFilters?.activityTags &&
            Object.keys(this.props.search.uiFilters.activityTags).length > 0;

        if (this.props.search?.filters && !hasActivityTagsInUiFilters) {
            // Legacy activity tags pill rendering from search.filters
            // Skipped when activityTags are managed via uiFilters
            this.props.search.filters.forEach((filter) => {
                // The activity_tags search param is a single string with comma separated values
                // so this might be "hunting" or it might be "hunting,camping"
                const maybeMultipleValues =
                    filter.attribute === 'activity_tags'
                        ? filter?.values?.[0]?.value
                        : '';

                if (maybeMultipleValues) {
                    const values = maybeMultipleValues.split(',');

                    values.forEach((value) => {
                        const displayTitle =
                            value.trim().toLowerCase() === 'featured-hunt-fish'
                                ? 'Featured Hunting & Fishing'
                                : toTitleCase(value);
                        pills.push(
                            <SearchFilterPill
                                key={filter.attribute}
                                filter={filter.attribute}
                                title={displayTitle}
                                onRemove={() => {
                                    const updatedFilters =
                                        this.props.search.filters.filter(
                                            (item) => item.attribute !== 'activity_tags'
                                        );

                                    this.props.dispatch(
                                        searchActions.updateSearchCriteria(
                                            'filters',
                                            updatedFilters
                                        )
                                    );

                                    this.props.dispatch(
                                        searchActions.navigateToSearchResults()
                                    );
                                }}
                            />
                        );
                    });
                }
            });
        }

        return pills;
    }

    render() {
        const pills = this.renderMainSearchFilters();
        const gaClickCategory = 'Search';
        const gaClickLabel = 'Active Filters Section';

        if (pills.length === 0) {
            return null;
        }

        return (
            <Inline space={['sm', 'sm', 'sm']} className={'filter-pills'}>
                {this.state.viewAll && pills}
                {!this.state.viewAll && (
                    <>
                        {pills.length > 3 ? pills.slice(0, 3) : pills}
                        {pills.length > 3 && (
                            <Button
                                appearance="link"
                                clickTagCategory={gaClickCategory}
                                clickTagAction={`Show ${
                                    pills.length - 3
                                } More Filters Button`}
                                clickTagLabel={gaClickLabel}
                                onClick={this.viewAllFilters}
                            >
                                {' '}
                                + {pills.length - 3} More Filters
                            </Button>
                        )}
                    </>
                )}
                <VerticalDivider />
                <Button
                    appearance="link"
                    onClick={this.clearAll}
                    clickTagCategory={gaClickCategory}
                    clickTagAction={`Clear All ${pills.length} Filters Button`}
                    clickTagLabel={gaClickLabel}
                >
                    Clear All
                </Button>
            </Inline>
        );
    }
}

function select(state) {
    return {
        search: state.search,
    };
}

export default connect(select)(SearchFilterPills);

// cSpell:ignore criterias, checkin, notreservable
